import { TRIP_STATE } from '@allocab/allocab-commons-shared-types';
import { RouteSolid } from '@allocab/allocab-icons';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { CheckBox } from '../../components/atomic/CheckBox';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { SecondaryButton } from '../../components/button/SecondaryButton';
import { FilterBadge } from '../../components/FilterBadge';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { t } from '../../services/translation/translation';
import { getTripStateName } from '../../utils/trip';

export const STATE_SEARCH_PARAMS_KEY = 'state';
const tripStates: TRIP_STATE[] = ['INCOMING', 'ONDUTY', 'ONPAYMENT', 'DONE', 'CANCELLED'];

type Props = {
  onChange: (_key: string, _value: string | undefined) => void;
};

export const TripStateFilter = ({ onChange }: Props) => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(false);
  const state = searchParams.get(STATE_SEARCH_PARAMS_KEY);
  const stateFromParams = state?.split(',') as TRIP_STATE[] | undefined;
  const [selectedStates, setSelectedStates] = useState<TRIP_STATE[]>(stateFromParams ?? tripStates);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const handleSave = () => {
    if (selectedStates.length < tripStates.length) {
      onChange(STATE_SEARCH_PARAMS_KEY, selectedStates.join(','));
    } else {
      onChange(STATE_SEARCH_PARAMS_KEY, undefined);
    }
    setIsVisible(false);
  };

  const handleReset = () => {
    setSelectedStates(tripStates);
    onChange(STATE_SEARCH_PARAMS_KEY, undefined);
    setIsVisible(false);
  };

  return (
    <div className="relative flex items-center justify-center">
      <FilterBadge onClick={() => setIsVisible(v => !v)} isActive={!!stateFromParams}>
        <RouteSolid width={12} height={12} className="fill-current" />
        <span>{stateFromParams ? stateFromParams.map(s => getTripStateName(s)).join(',') : t('common.status')}</span>
      </FilterBadge>
      <div
        ref={ref}
        className={twJoin(
          'absolute top-10 z-10 w-48 bg-white rounded-lg shadow dark:bg-gray-700',
          !isVisible && 'hidden',
        )}
      >
        <ul className="p-3 space-y-1 text-sm text-gray-700 dark:text-gray-200">
          {tripStates.map(state => (
            <li key={state}>
              <CheckBox
                id={`state-filter-${state}`}
                checked={selectedStates.includes(state)}
                onChange={value =>
                  value
                    ? setSelectedStates(p => [...p, state])
                    : setSelectedStates(p => (p.length === 1 ? p : p.filter(s => s !== state)))
                }
                label={getTripStateName(state)}
              />
            </li>
          ))}
        </ul>
        <div className="space-y-2 px-2 mb-2">
          <PrimaryButton className="w-full" onClick={handleSave}>
            {t('common.apply')}
          </PrimaryButton>

          <SecondaryButton className="w-full" onClick={handleReset}>
            {t('common.reset')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};
